// 81 §6/§7/§9 — A PERSON'S OWN RECORD OF WHO CAME IN AS HIM.
//
// §7: "The list, in Settings, beside brief 80's card: his own successful logins
//  and his own failed attempts, with time, place, device and outcome."
//
// ── IT IS NOT GRANTABLE, FOR 80 §2's REASON ────────────────────────────────
//
// Like `AuthFactorsCard` beside it, this renders for every authenticated
// person with no `settings.personal.*` key in front of it. An administrator who
// could withhold a person's view of his own sign-ins would be withholding the
// one thing that lets that person notice somebody else using his account —
// and §7's whole argument is that "nobody audits a log, and everybody notices a
// date they were asleep on".
//
// ── WHAT IT SAYS ABOUT WHAT IT SHOWS ───────────────────────────────────────
//
// §1: "`user agent` and `location` are self-reported or inferred. Both are
//  indications and neither is evidence. EVERY SCREEN THAT SHOWS THEM SAYS SO IN
//  ITS OWN WORDS." §9: "The record states its PURPOSE — security of the firm's
//  systems — and its RETENTION PERIOD, in the screens a person reads."
//
// Both are here, and the retention period is INTERPOLATED FROM THE SERVER's
// answer rather than typed: `retentionDays` travels with the data, so changing
// `authAttemptSweep.RETENTION_DAYS` cannot leave this screen promising a period
// the sweep no longer keeps.
import { useState, useEffect } from "react";
import { useTranslation } from "../i18n";
import { FS } from "../lib/tokens";
import { getAuthAttempts } from "../api";
import type { AuthAttemptRow, AuthAttemptsResponse } from "../api";
import { withCount } from "../lib/pluralVars"; // 209 §7 — a sentence's number, read once, as its placeholder and as `count`

/** What a rejected api call carries — see AuthFactorsCard for the seam. */
type ApiFailure = { message?: string };

/**
 * §2's verdicts, as the words a person reads.
 *
 * KEYED, NOT SENTENCED HERE: each returns a literal `t()` key so
 * `i18n-keys-complete.js` can see it. A map from code to key would make every
 * one of them a computed site — the blind spot that lint names in its own
 * header, and the one `auth-shape-texts.js` exists because of.
 */
function outcomeText(t: (k: string, v?: Record<string, unknown>) => string, a: AuthAttemptRow): string {
  switch (a.outcome) {
    case "success":
      return t("attempts.outcome.success", { fallback: "Signed in" });
    case "wrong_password":
      return t("attempts.outcome.wrong_password", { fallback: "Wrong password" });
    case "matches_former_password":
      // THE LEAK SIGNAL, and it says what it means rather than naming a code.
      return t("attempts.outcome.matches_former_password", {
        fallback: "A password you used to have — treat it as leaked",
      });
    case "password_ok_second_step_failed":
      return t("attempts.outcome.second_step_failed", {
        fallback: "Your password was right, the second step was not",
      });
    case "password_ok_account_blocked":
      return t("attempts.outcome.account_blocked", {
        fallback: "Your password was right, but your account was closed to sign-ins",
      });
    default:
      return t("attempts.outcome.unknown_address", { fallback: "No such account" });
  }
}

/** One row, as prose rather than as a table of codes. */
function AttemptRow({ a, hasCity }: { a: AuthAttemptRow; hasCity: boolean }) {
  const { t, locale } = useTranslation();
  const when = new Date(a.lastAt);
  const good = a.outcome === "success";
  const loud = a.outcome === "matches_former_password" || a.outcome === "password_ok_account_blocked";

  // §3 — a row can stand for hundreds of attempts. Saying "1" would be noise;
  // saying nothing where the number is 340 would be a lie by omission.
  const times = a.count > 1
    ? t("attempts.times", withCount(a.count, "n", { fallback: `, ${a.count} times` }))
    : "";

  const place = a.location.country
    ? (hasCity && a.location.city ? `${a.location.city}, ${a.location.country}` : a.location.country)
    : t("attempts.place_unknown", { fallback: "an unrecognised network" });

  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        alignItems: "baseline",
        fontSize: FS.s11,
        padding: "6px 0",
        borderBottom: "1px solid rgba(255,255,255,.04)",
      }}
    >
      <span style={{ color: "var(--tm)", minWidth: 132, fontVariantNumeric: "tabular-nums" }}>
        {when.toLocaleString(locale, {
          day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
        })}
      </span>
      <span style={{ flex: 1, color: loud ? "var(--rd)" : good ? "var(--tp)" : "var(--ts)" }}>
        {outcomeText(t, a)}
        {times}
        {a.outcome === "matches_former_password" && a.matchedVintage && (
          <span style={{ color: "var(--tm)" }}>
            {t("attempts.vintage", {
              d: new Date(a.matchedVintage).toLocaleDateString(locale),
              fallback: ` (you stopped using it on ${new Date(a.matchedVintage).toLocaleDateString(locale)})`,
            })}
          </span>
        )}
      </span>
      <span style={{ color: "var(--tm)" }}>{place}</span>
      <span style={{ color: "var(--tm)", fontVariantNumeric: "tabular-nums" }}>{a.ip}</span>
    </div>
  );
}

export default function AuthAttemptsCard() {
  const { t } = useTranslation();
  const [data, setData] = useState<AuthAttemptsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Mount only, and the load is INLINE — a function re-created every render
  // cannot be a dependency, and nothing this effect sets appears in its own
  // deps (scripts/lint/effect-sets-its-own-dep.js). Same shape as
  // AuthFactorsCard, deliberately.
  useEffect(() => {
    let live = true;
    getAuthAttempts()
      .then((d) => { if (live) setData(d); })
      .catch((e) => {
        if (live) {
          setErr(String((e as ApiFailure)?.message || "").trim()
            || t("attempts.load_failed", { fallback: "Could not read your sign-in history." }));
        }
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  const rows = data?.attempts || [];

  return (
    <>
      {/* §9 — the purpose and the period, in the screen a person reads. */}
      <p style={{ fontSize: FS.s11_5, color: "var(--ts)", margin: "6px 0 4px", lineHeight: 1.6 }}>
        {t("attempts.lede", {
          fallback:
            "Every sign-in to your account, and every attempt that failed. It is kept to protect the firm's systems, and nobody — not an administrator, not you — can delete a line of it.",
        })}
      </p>
      <p style={{ fontSize: FS.s11, color: "var(--tm)", margin: "0 0 10px", lineHeight: 1.5 }}>
        {t("attempts.retention", withCount(data?.retentionDays ?? 365, "n", { fallback: `Each entry is removed automatically after ${data?.retentionDays ?? 365} days.`,
        }))}
        {" "}
        {/* §1 — the caveat, in this screen's own words, and CC BY 4.0's credit
            in the same sentence (docs/dependency-audit-2026-08-14-geoip.md §4). */}
        {t("attempts.indication", {
          fallback:
            "The place is worked out from the network address using an offline database from DB-IP.com, and the device is what the browser reports: both are indications, not proof. A VPN or a mobile connection can put you in another country.",
        })}
      </p>

      {loading && (
        <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>{t("common.loading", { fallback: "Loading…" })}</p>
      )}
      {err && <div style={{ fontSize: FS.s11, color: "var(--dg, #e5484d)", margin: "0 0 8px" }}>{err}</div>}

      {!loading && !err && rows.length === 0 && (
        <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>
          {t("attempts.empty", { fallback: "Nothing recorded yet." })}
        </p>
      )}

      {rows.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {rows.map((a) => (
            <AttemptRow key={a.id} a={a} hasCity={!!data?.location?.hasCity} />
          ))}
        </div>
      )}

      {/* A capped list must say it is capped, or it reads as complete. */}
      {data && rows.length >= data.limit && (
        <p style={{ fontSize: FS.s10_5, color: "var(--tm)", marginTop: 8 }}>
          {t("attempts.capped", withCount(data.limit, "n", { fallback: `Showing the most recent ${data.limit} entries.`,
          }))}
        </p>
      )}
    </>
  );
}
